import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { trackPixel } from '@/lib/metaPixel';
import axios from 'axios';
import { User, MapPin, CalendarDays, ChevronRight, ChevronLeft, Check, Phone, Mail, Home, Loader2 } from 'lucide-react';
import SlotPicker from './SlotPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'About You', icon: User },
  { id: 2, label: 'Your Address', icon: MapPin },
  { id: 3, label: 'Schedule Visit', icon: CalendarDays },
];

const SERVICE_OPTIONS = [
  { value: 'onboarding', label: 'Getting Started', desc: 'New to smartphones or apps' },
  { value: 'troubleshooting', label: 'Fix a Problem', desc: 'Something not working right' },
  { value: 'training', label: 'Learn an App', desc: 'WhatsApp, UPI, video calls' },
  { value: 'concierge', label: 'Get Help With a Task', desc: 'Bookings, forms, payments' },
];

const emptyForm = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  city: '',
  address: '',
  preferred_date: '',
  preferred_time: '',
  service_type: 'onboarding',
  additional_notes: '',
  slot_date: '',
  slot_hour: null,
};

const DigiMitraBookingForm = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null); // { city, message } | null

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceed = () => {
    if (step === 1) return form.customer_name.trim() && form.customer_phone.trim();
    if (step === 2) return form.city.trim() && form.address.trim();
    if (step === 3) return form.slot_date && form.slot_hour !== null && form.service_type;
    return false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Derive legacy preferred_date/time from slot for email template compat
      const hh = String(form.slot_hour).padStart(2, '0');
      const payload = {
        ...form,
        preferred_date: form.slot_date,
        preferred_time: `${hh}:00`,
        customer_email: form.customer_email?.trim() || null,
        additional_notes: form.additional_notes?.trim() || null,
      };
      const res = await axios.post(`${API}/digimitra/book`, payload);
      if (res.data?.waitlisted) {
        setWaitlisted({ city: form.city.trim(), message: res.data.message });
        trackPixel('Lead', { content_name: 'DigiMitra Waitlist', content_category: 'DigiMitra', city: form.city.trim() });
      } else {
        setSubmitted(true);
        trackPixel('Lead', { content_name: 'DigiMitra Booking', content_category: 'DigiMitra', value: 499, currency: 'INR', city: form.city.trim() });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    if (!val) {
      setTimeout(() => {
        setStep(1);
        setForm({ ...emptyForm });
        setSubmitted(false);
        setWaitlisted(null);
      }, 300);
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="dm-wizard" data-testid="digimitra-booking-modal">
        <VisuallyHidden.Root><DialogTitle>Book a DigiMitra Visit</DialogTitle><DialogDescription>Schedule a 1-hour home visit in 3 steps.</DialogDescription></VisuallyHidden.Root>
        {waitlisted ? (
          <div className="dm-success" data-testid="digimitra-waitlist">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
            <h2>Coming soon to {waitlisted.city}!</h2>
            <p style={{ maxWidth: 420, margin: '0 auto 18px' }}>{waitlisted.message}</p>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>We've saved your details and will WhatsApp <strong>{form.customer_phone}</strong> as soon as we launch in your city.</p>
            <Button className="dm-done-btn" onClick={() => handleClose(false)} data-testid="digimitra-waitlist-close">
              Got it
            </Button>
          </div>
        ) : submitted ? (
          <div className="dm-success" data-testid="digimitra-success">
            <div className="dm-success-icon">
              <Check size={40} strokeWidth={3} />
            </div>
            <h2>Booking Confirmed!</h2>
            <p>We'll call you on <strong>{form.customer_phone}</strong> to confirm your visit.</p>
            <Button className="dm-done-btn" onClick={() => handleClose(false)} data-testid="digimitra-done-btn">
              Done
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="dm-header">
              <h2 data-testid="digimitra-title">Book a DigiMitra Visit</h2>
              <p className="dm-price">1-hour home visit &middot; <span>&#8377;499</span></p>
            </div>

            {/* Progress */}
            <div className="dm-progress" data-testid="digimitra-progress">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`dm-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                    <div className="dm-step-circle">
                      {isDone ? <Check size={18} strokeWidth={3} /> : <Icon size={18} />}
                    </div>
                    <span className="dm-step-label">{s.label}</span>
                  </div>
                );
              })}
              <div className="dm-progress-bar">
                <div className="dm-progress-fill" style={{ width: `${((step - 1) / 2) * 100}%` }} />
              </div>
            </div>

            {/* Step Content */}
            <div className="dm-body">
              {step === 1 && (
                <div className="dm-fields" data-testid="digimitra-step-1">
                  <div className="dm-field">
                    <label htmlFor="dm-name">
                      <User size={16} /> Your Name
                    </label>
                    <Input
                      id="dm-name"
                      data-testid="dm-name-input"
                      placeholder="e.g. Ramesh Kumar"
                      value={form.customer_name}
                      onChange={(e) => set('customer_name', e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="dm-field">
                    <label htmlFor="dm-phone">
                      <Phone size={16} /> Phone Number
                    </label>
                    <Input
                      id="dm-phone"
                      data-testid="dm-phone-input"
                      type="tel"
                      placeholder="e.g. 98765 43210"
                      value={form.customer_phone}
                      onChange={(e) => set('customer_phone', e.target.value)}
                    />
                  </div>
                  <div className="dm-field dm-optional">
                    <label htmlFor="dm-email">
                      <Mail size={16} /> Email <span className="dm-opt-tag">optional</span>
                    </label>
                    <Input
                      id="dm-email"
                      data-testid="dm-email-input"
                      type="email"
                      placeholder="e.g. ramesh@gmail.com"
                      value={form.customer_email}
                      onChange={(e) => set('customer_email', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="dm-fields" data-testid="digimitra-step-2">
                  <div className="dm-field">
                    <label htmlFor="dm-city">
                      <MapPin size={16} /> City
                    </label>
                    <Input
                      id="dm-city"
                      data-testid="dm-city-input"
                      placeholder="e.g. New Delhi"
                      value={form.city}
                      onChange={(e) => set('city', e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="dm-field">
                    <label htmlFor="dm-address">
                      <Home size={16} /> Full Address
                    </label>
                    <Textarea
                      id="dm-address"
                      data-testid="dm-address-input"
                      placeholder="House / Flat no., Street, Landmark..."
                      value={form.address}
                      onChange={(e) => set('address', e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="dm-fields" data-testid="digimitra-step-3">
                  <SlotPicker
                    date={form.slot_date}
                    onDateChange={(v) => set('slot_date', v)}
                    serviceType="digimitra"
                    selectedHour={form.slot_hour}
                    onSelectHour={(h) => set('slot_hour', h)}
                    accentClass="slot-accent-dm"
                    testIdPrefix="dm-slot"
                  />

                  <div className="dm-field">
                    <label>What do you need help with?</label>
                    <div className="dm-service-cards" data-testid="dm-service-options">
                      {SERVICE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`dm-service-card ${form.service_type === opt.value ? 'selected' : ''}`}
                          data-testid={`dm-service-${opt.value}`}
                          onClick={() => set('service_type', opt.value)}
                        >
                          <strong>{opt.label}</strong>
                          <span>{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="dm-field dm-optional">
                    <label htmlFor="dm-notes">
                      Anything else? <span className="dm-opt-tag">optional</span>
                    </label>
                    <Textarea
                      id="dm-notes"
                      data-testid="dm-notes-input"
                      placeholder="Tell us more about what you need..."
                      value={form.additional_notes}
                      onChange={(e) => set('additional_notes', e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="dm-footer">
              {step > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="dm-back-btn"
                  onClick={() => setStep(step - 1)}
                  data-testid="dm-back-btn"
                >
                  <ChevronLeft size={18} /> Back
                </Button>
              )}
              <div className="dm-footer-spacer" />
              {step < 3 ? (
                <Button
                  type="button"
                  className="dm-next-btn"
                  disabled={!canProceed()}
                  onClick={() => setStep(step + 1)}
                  data-testid="dm-next-btn"
                >
                  Next <ChevronRight size={18} />
                </Button>
              ) : (
                <Button
                  type="button"
                  className="dm-submit-btn"
                  disabled={!canProceed() || loading}
                  onClick={handleSubmit}
                  data-testid="dm-submit-btn"
                >
                  {loading ? <><Loader2 size={18} className="dm-spin" /> Booking...</> : <>Confirm Booking <Check size={18} /></>}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DigiMitraBookingForm;
